import moment from 'moment';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, HideText, Icon } from '@fashionnovateam/nova-ui-toolkit';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import { FilterSearch } from '../FilterSearch/FilterSearch.tsx';
import CountryList from '../../Lib/Constants/countries';
import FactoriesDataGrid from '../Admin/FactoriesDataGrid.tsx';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';

const filterMap = {
  all: {},
  pendingReview: {
    rejection_reason: { value: null },
    submitted_at: { value: null, op: 'ne' },
    approved_at: { value: null },
  },
  rejected: {
    rejection_reason: { value: null, op: 'ne' },
  },
  approved: {
    approved_at: { value: null, op: 'ne' },
  },
};

const filterItems = [
  {
    icon: 'file/file-text',
    value: 'all',
    label: 'All',
  },
  {
    icon: 'circle-dot',
    value: 'pendingReview',
    label: 'Pending Review',
  },
  {
    icon: 'check/check-tick-circle',
    value: 'approved',
    label: 'Approved',
  },
  {
    icon: 'ban/ban-right',
    value: 'rejected',
    label: 'Rejected',
  },
];

const countryFilterItems = [
  {
    value: '',
    label: 'All Countries',
  },
  ...CountryList.map((country) => ({
    value: country.value,
    label: country.text,
  })),
];

/**
 * Creates the table headers from objects imported here.
 *
 * @param {arrayOf} headerArr array of header value objects
 *  @property {object} header individual objects with header values
 *    @property {string} label the label value
 *    @property {number} key unique key for react components
 *
 * @returns {object} header cell components to be used by the table.
 */

export default function ComplianceFactories(props) {
  const [params, setParams] = useState({
    ...decodeLocation(props.location.search),
    search: '',
    page: 1,
    sortBy: 'createdAt',
    sortOrder: 'DESC',
    filters: 'all',
    country: '',
  });

  useEffect(() => {
    props.getFactories({
      ...params,
      country: undefined,
      filters: {
        ...filterMap[params.filters],
        country: params.country ? { value: params.country } : undefined,
      },
    });
  }, [params]);

  const exportExpiringFactories = () => {
    const p = {
      limit: 100000,
      filters: {
        audit_expiration_date: {
          value: moment().add(1, 'months').toDate().toISOString(),
          op: 'lt',
        },
      },
    };
    props.exportFactories(p);
  };

  const exportSearchFactories = () => {
    props.exportFactories({
      ...params,
      country: undefined,
      filters: {
        ...filterMap[params.filters],
        country: params.country ? { value: params.country } : undefined,
      },
      limit: 1000000,
      page: 1,
    });
  };

  const perms = useMemo(() => {
    const canExportExpiring = hasPermissionTo(
      'compliance-factories',
      props.currentUser.role,
      Actions.READ,
      'EXPORT_EXPIRING',
    );
    const canExport = hasPermissionTo(
      'compliance-factories',
      props.currentUser.role,
      Actions.READ,
      'EXPORT',
    );
    return { canExportExpiring, canExport };
  }, [props.currentUser]);

  return (
    <Page.Container withoutTwp>
      <Header.Major icon="building/building-apartment-two" title="Factories">
        {perms.canExportExpiring && (
          <Button color="secondary" size="sm" onClick={exportExpiringFactories}>
            <Icon name="download/download-down" />
            <HideText notAbove="lg">
              Export{'\u00A0'}Upcoming{'\u00A0'}Audit{'\u00A0'}Expirations
            </HideText>
          </Button>
        )}
      </Header.Major>
      <Header.Minor
        filter={{
          value: params.filters,
          onChange: (value) =>
            setParams((p) => ({
              ...p,
              filters: value,
            })),
          items: filterItems,
        }}
        search={{
          value: params.search,
          onChange: debounce(
            (value) => setParams((p) => ({ ...p, search: value })),
            500,
          ),
        }}
      >
        <FilterSearch
          withSearch
          value={params.country}
          items={countryFilterItems}
          emptyLabel="All Countries"
          label="Country"
          onAddValue={(value) => setParams((p) => ({ ...p, country: value }))}
          onRemoveValue={() => setParams((p) => ({ ...p, country: '' }))}
        />
        {perms.canExport && (
          <Button color="secondary" size="sm" onClick={exportSearchFactories}>
            <Icon name="download/download-down" />
            <HideText notAbove="lg">Export</HideText>
          </Button>
        )}
      </Header.Minor>
      <Page.Content>
        <FactoriesDataGrid
          currentUser={props.currentUser}
          factories={props.factories}
          rowCount={props.count}
          loading={props.loading}
          paginationModel={{
            page: params?.page ? params.page - 1 : 0,
            pageSize: 15,
          }}
          pageSizeOptions={[15]}
          sortModel={
            params?.sortBy
              ? [
                  {
                    field: params.sortBy,
                    sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                  },
                ]
              : []
          }
          onSortModelChange={(model) => {
            const sortItem = model[0];
            setParams((p) => ({
              ...p,
              sortBy: sortItem?.field || undefined,
              sortOrder: sortItem?.sort.toUpperCase() || undefined,
            }));
          }}
          onPaginationModelChange={(model) =>
            setParams((p) => ({ ...p, page: model.page + 1 }))
          }
        />
      </Page.Content>
    </Page.Container>
  );
}

ComplianceFactories.propTypes = {
  count: PropTypes.number.isRequired,
  factories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
  loading: PropTypes.bool.isRequired,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  getFactories: PropTypes.func.isRequired,
  exportFactories: PropTypes.func.isRequired,
};
